// src/components/trainingDue/TrainingDue.jsx

import React, { useEffect, useState } from 'react';
import { Box, Typography, Chip, IconButton, FormControlLabel, Checkbox } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { mergeDataGridStyles } from '../../../styles/dataGridStyles';
import { useTrainingDue } from '../../../hooks/useTrainingDue';
import { useCurrentWeekRosterEmployees } from '../../../hooks/useCurrentWeekRosterEmployees';
import { formatUSDateNoTime } from "../../../utils/utils";
import { getAllMepForms } from '../../../services/mepFormService';
import { getAllFtForms } from '../../../services/ftFormService';
import { getAllWeldingForms } from '../../../services/weldingFormService';
import { getAllTrainingDueForms } from '../../../services/trainingDueDefaultFormService';
import { STATUS_PROCESS, STATUS_FINISH, STATUS_SEND } from '../../../config/constants';
import practicalEvaluationService from '../../../services/practicalEvaluationService';

export default function TrainingDue() {
    const trainingDueList = useTrainingDue();
    const { rosterEmployees, hasRoster } = useCurrentWeekRosterEmployees();
    const [isNarrow, setIsNarrow] = useState(false);
    const [displayData, setDisplayData] = useState([]);
    const [filterByRoster, setFilterByRoster] = useState(false);
    const [practicalEvalTypes, setPracticalEvalTypes] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        const handleResize = () => {
            setIsNarrow(window.innerWidth < 900);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const baseData = Array.isArray(trainingDueList)
            ? trainingDueList.filter(item => item && item.id != null)
            : [];

        (async () => {
            try {
                const [allMep, allFt, allWelding, allDefault, peTypes] = await Promise.all([
                    getAllMepForms(),
                    getAllFtForms(),
                    getAllWeldingForms(),
                    getAllTrainingDueForms(),
                    practicalEvaluationService.getTypesFromIndexedDB()
                ]);
                setPracticalEvalTypes(peTypes || []);

                const mepMap = {};
                allMep.forEach(form => {
                    mepMap[form.trainingDueId] = form.status;
                });
                const ftMap = {};
                allFt.forEach(form => {
                    ftMap[form.trainingDueId] = form.status;
                });
                const weldingMap = {};
                allWelding.forEach(form => {
                    weldingMap[form.trainingDueId] = form.status;
                });
                const defaultMap = {};
                allDefault.forEach(form => {
                    defaultMap[form.id] = form.status;
                });


                // Practical evaluation forms status map
                const allPe = await practicalEvaluationService.getAllFromIndexedDB();
                const peMap = {};
                (allPe || []).forEach(form => {
                    if (form.trainingDueId) {
                        peMap[form.trainingDueId] = form.status;
                    }
                });

                const merged = baseData.map(row => {
                    const course = row.course || '';
                    let rowStatus = null;
                    // Check if course matches a practical evaluation type
                    const matchedPeType = (peTypes || []).find(t =>
                        t.defaultCourseName && course.toLowerCase().includes(t.defaultCourseName.toLowerCase())
                    );
                    if (matchedPeType) {
                        // Check PE forms first, fall back to old MEP forms for backward compat
                        rowStatus = peMap[row.id] || mepMap[row.id] || null;
                    } else if (course.includes('Forklift')) {
                        rowStatus = ftMap[row.id] || null;
                    } else if (course.includes('Welding')) {
                        rowStatus = weldingMap[row.id] || null;
                    } else {
                        rowStatus = defaultMap[row.id] || null;
                    }
                    return { ...row, formStatus: rowStatus };
                });

                let filtered = merged.filter(item => item.formStatus !== STATUS_SEND);
                
                // Apply roster filter if enabled
                if (filterByRoster && hasRoster && rosterEmployees.length > 0) {
                    console.log('Roster employees for filtering:', rosterEmployees);
                    console.log('Training due items before filter:', filtered.length);
                    
                    // Get all employee names from roster (handle different data structures)
                    const rosterEmployeeNames = rosterEmployees.map(emp => {
                        // Handle both string names and objects with name/fullName properties
                        if (typeof emp === 'string') return emp.toLowerCase().trim();
                        if (emp.fullName) return emp.fullName.toLowerCase().trim();
                        if (emp.name) return emp.name.toLowerCase().trim();
                        return '';
                    }).filter(name => name);
                    
                    console.log('Roster employee names for matching:', rosterEmployeeNames);
                    
                    // Filter training due list to only include employees in roster
                    filtered = filtered.filter(item => {
                        const itemName = (item.fullName || '').toLowerCase().trim();
                        
                        // Handle "Last, First" format in training data
                        const nameParts = itemName.split(',').map(part => part.trim());
                        const itemNameNormalized = nameParts.length === 2 
                            ? `${nameParts[1]} ${nameParts[0]}`.toLowerCase() // Convert to "First Last"
                            : itemName;
                        
                        const isMatch = rosterEmployeeNames.some(rosterName => {
                            // Also normalize roster name just in case
                            const rosterParts = rosterName.split(',').map(part => part.trim());
                            const rosterNameNormalized = rosterParts.length === 2 
                                ? `${rosterParts[1]} ${rosterParts[0]}`.toLowerCase()
                                : rosterName;
                            
                            // Check multiple matching strategies
                            return itemName === rosterName || 
                                   itemNameNormalized === rosterNameNormalized ||
                                   itemNameNormalized === rosterName ||
                                   itemName === rosterNameNormalized ||
                                   // Also check if names contain each other (partial matches)
                                   itemName.includes(rosterName) || 
                                   rosterName.includes(itemName) ||
                                   itemNameNormalized.includes(rosterNameNormalized) ||
                                   rosterNameNormalized.includes(itemNameNormalized);
                        });
                        
                        if (!isMatch && itemName) {
                            console.log(`No match for training item: "${itemName}" (normalized: "${itemNameNormalized}")`);
                        }
                        
                        return isMatch;
                    });
                    
                    console.log('Training due items after filter:', filtered.length);
                }
                
                filtered.sort((a, b) => {
                    const topStatuses = [STATUS_PROCESS, STATUS_FINISH];
                    const aIsTop = topStatuses.includes(a.formStatus);
                    const bIsTop = topStatuses.includes(b.formStatus);
                    if (aIsTop && !bIsTop) return -1;
                    if (!aIsTop && bIsTop) return 1;
                    return (a.course || '').localeCompare(b.course || '');
                });

                setDisplayData(filtered);
            } catch (err) {
                setDisplayData(baseData);
            }
        })();
    }, [trainingDueList, filterByRoster, hasRoster, rosterEmployees]);

    const columnsBase = [
        { field: 'course', headerName: 'Training Course', flex: 2, minWidth: 200 },
        { field: 'fullName', headerName: 'Employee Name', flex: 2, minWidth: 150 },
        { field: 'division', headerName: 'Division', flex: 1, minWidth: 100 },
        {
            field: 'dateDue',
            headerName: 'Training Due',
            flex: 1,
            minWidth: 120,
            renderCell: params => (
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    {formatUSDateNoTime(params.value)}
                </Typography>
            ),
        },
        {
            field: 'formStatus',
            headerName: 'Status',
            flex: 1,
            minWidth: 120,
            renderCell: ({ value }) => {
                if (value === STATUS_PROCESS) {
                    return (
                        <Chip
                            label="PROCESS"
                            color="warning"
                            variant="filled"
                            sx={{
                                fontWeight: 'bold',
                                fontSize: '0.75rem',
                                height: '24px',
                                minWidth: '70px',
                            }}
                            size="small"
                        />
                    );
                }
                if (!value) {
                    return (
                        <Chip
                            label="NOT STARTED"
                            color="default"
                            variant="filled"
                            sx={{
                                fontSize: '0.75rem',
                                height: '24px',
                                minWidth: '90px',
                            }}
                            size="small"
                        />
                    );
                }
                return (
                    <Chip
                        label={value.toUpperCase()}
                        color="info"
                        variant="filled"
                        sx={{
                            fontSize: '0.75rem',
                            height: '24px',
                            minWidth: '70px',
                        }}
                        size="small"
                    />
                );
            }
        }
    ];

    const columns = isNarrow ? columnsBase : columnsBase;

    const handleRowClick = params => {
        const row = params.row;
        const course = row.course || '';

        // Check if course matches any Practical Evaluation Type FIRST (MEWPS + Forklift types)
        const matchedType = practicalEvalTypes.find(t =>
            t.defaultCourseName && course.toLowerCase().includes(t.defaultCourseName.toLowerCase())
        );
        if (matchedType) {
            navigate(`/training-due/practical-evaluation/${matchedType.slug}/new`, {
                state: {
                    selectedTrainingDue: row,
                    evaluationTypeSlug: matchedType.slug,
                    evaluationTypeName: matchedType.name
                }
            });
            return;
        }

        // Old forklift courses (class 1,2,3,5,7) that don't match any PE type
        const isForklift = course.includes('Forklift');
        if (isForklift) {
            navigate('/training-due/operator-practical-evaluation', { state: { selectedTrainingDue: row } });
            return;
        }

        const isWelding = course.includes('Welding');
        if (isWelding) {
            navigate('/training-due/welding', { state: { selectedTrainingDue: row } });
            return;
        }

        navigate('/training-due/default', { state: { selectedTrainingDue: row } });
    };

    return (
        <Box sx={{ 
            position: 'fixed',
            top: '60px', // header height
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: 'background.default'
        }}>
            {/* Header - Fixed Height */}
            <Box 
                sx={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                    flex: '0 0 auto'
                }}
            >
                <Box display="flex" alignItems="center" gap={2}>
                    <IconButton onClick={() => navigate('/')} color="primary" size="large">
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h5">Training Due</Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                            {new Date().toLocaleDateString('en-US', { 
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })}
                        </Typography>
                    </Box>
                </Box>
                <Box display="flex" gap={2} alignItems="center">
                    {hasRoster && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={filterByRoster}
                                    onChange={(e) => setFilterByRoster(e.target.checked)}
                                    size="small"
                                />
                            }
                            label="Show only roster employees"
                            sx={{ 
                                mr: 2,
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '0.875rem'
                                }
                            }}
                        />
                    )}
                    <Typography variant="caption" color="text.secondary">
                        {displayData.length} training items
                    </Typography>
                </Box>
            </Box>

            {/* DataGrid Container - Flexible Height */}
            <Box 
                sx={{ 
                    flex: '1 1 auto',
                    overflow: 'hidden',
                    p: 2
                }}
            >
                <DataGrid
                    rows={displayData}
                    columns={columns}
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: 25 }
                        }
                    }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    disableSelectionOnClick
                    getRowId={row => row.id}
                    rowHeight={52}
                    onRowClick={handleRowClick}
                    sx={mergeDataGridStyles({
                        '& .MuiDataGrid-row:hover': {
                            cursor: 'pointer',
                            backgroundColor: 'action.hover',
                        },
                    })}
                />
            </Box>
        </Box>
    );
}
